import React, { useState, useEffect } from 'react';
import { HexColorPicker } from 'react-colorful';
import { Save, X } from 'lucide-react';

interface AdvancedColorPickerProps {
  color: string;
  onChange: (color: string) => void;
  onClose?: () => void;
}

const RECENT_COLORS_KEY = 'frahda_recent_colors';
const MAX_RECENT_COLORS = 10;

export const AdvancedColorPicker: React.FC<AdvancedColorPickerProps> = ({
  color,
  onChange,
  onClose
}) => {
  const [recentColors, setRecentColors] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState(color);

  useEffect(() => {
    const saved = localStorage.getItem(RECENT_COLORS_KEY);
    if (saved) {
      try {
        setRecentColors(JSON.parse(saved));
      } catch (e) {
        console.error('Error parsing recent colors', e);
      }
    }
  }, []);

  useEffect(() => {
    setInputValue(color);
  }, [color]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVal = e.target.value;
    setInputValue(newVal);
    if (/^#[0-9A-F]{6}$/i.test(newVal)) {
      onChange(newVal);
    }
  };

  const saveColor = () => {
    if (!recentColors.includes(color)) {
      const newColors = [color, ...recentColors].slice(0, MAX_RECENT_COLORS);
      setRecentColors(newColors);
      localStorage.setItem(RECENT_COLORS_KEY, JSON.stringify(newColors));
    }
  };

  const selectRecent = (c: string) => {
    onChange(c);
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow-xl border border-gray-200 w-full max-w-xs mx-auto z-50 relative">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-sm font-semibold text-gray-700">Selector de Color</h3>
        {onClose && (
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        )}
      </div>

      <div className="flex flex-col items-center mb-4">
        <div className="mb-3 w-full flex justify-center">
          <HexColorPicker color={color} onChange={onChange} style={{ width: '100%' }} />
        </div>
        
        <div className="w-full flex items-center space-x-2">
          <div 
            className="w-8 h-8 rounded border border-gray-300 shadow-sm"
            style={{ backgroundColor: color }}
          />
          <input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            className="flex-1 px-3 py-1 border border-gray-300 rounded text-sm uppercase font-mono"
            placeholder="#000000"
          />
        </div>
      </div>

      <div className="mb-2">
        <div className="flex justify-between items-center mb-2">
          <span className="text-xs font-medium text-gray-500">Colores Recientes</span>
          <button 
            onClick={saveColor}
            className="flex items-center text-xs text-blue-600 hover:text-blue-800 bg-blue-50 px-2 py-1 rounded"
            title="Guardar color actual"
            type="button"
          >
            <Save size={14} className="mr-1" />
            Guardar
          </button>
        </div>
        
        <div className="flex flex-wrap gap-2 min-h-[2rem]">
          {recentColors.map((c, idx) => (
            <button
              key={idx}
              className="w-6 h-6 rounded-full border border-gray-200 shadow-sm hover:scale-110 transition-transform focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500"
              style={{ backgroundColor: c }}
              onClick={() => selectRecent(c)}
              title={c}
              type="button"
            />
          ))}
          {recentColors.length === 0 && (
            <span className="text-xs text-gray-400 italic py-1">No hay colores guardados</span>
          )}
        </div>
      </div>
    </div>
  );
};
